import { ChangeDetectorRef, Directive, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import {
  BaseSiteService,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  isNotNullable,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_GUEST
} from '@spartacus/core'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { UntypedFormGroup } from '@angular/forms'
import { MbConstants } from '../../../shared/mb-constants'
import { MbCheckoutStepService } from '../../services/mb-checkout-step.service'
import { MbCheckoutNavigationService } from '../../services/mb-checkout-navigation.service'
import { MbCheckoutContactDetailsFacade } from '../../facades/mb-checkout-contact-details.facade'
import {
  AcademicDegree,
  JobTitle,
  MbCheckoutContactDetailsModel,
  MbCheckoutTypes,
  Salutation,
  SortableLabel
} from '../../../models'
import {
  MbAcademicDegreesConnector,
  MbCheckoutContactDetailsConnector,
  MbJobTitlesConnector,
  MbSalutationConnector
} from '../../connectors'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { CheckoutContactDetailsSetEvent } from '../../../analytics/events/mb-checkout.events'

const sortAscLabels = (a: SortableLabel, b: SortableLabel) => (a.sortOrder || 0) - (b.sortOrder || 0)

@Directive()
export abstract class MbCheckoutContactDetailsBase implements OnInit, OnDestroy {

  contactDetails: MbCheckoutContactDetailsModel = {}
  contactDetailsForm: UntypedFormGroup = this.convertCheckoutContactDetailsModel2Form()
  selectedAcademicDegree: AcademicDegree | undefined = {
    code: MbConstants.ACADEMIC_DEGREE,
    name: MbConstants.ACADEMIC_DEGREE
  }
  selectedRole: JobTitle | undefined = { code: MbConstants.ROLE, name: MbConstants.ROLE }
  selectedSalutation: Salutation | undefined
  academicDegrees$ = this.academicDegreesConnector.get().pipe(
    filter(isNotNullable),
    map(academicDegrees => academicDegrees.sort(sortAscLabels))
  )
  jobTitles$ = this.jobTitlesConnector.get().pipe(
    filter(isNotNullable),
    map(jobTitles => jobTitles.sort(sortAscLabels))
  )
  titles$ = this.titlesConnector.get().pipe(
    filter(isNotNullable),
    map(titles => titles?.sort(sortAscLabels))
  )
  subscription: Subscription = new Subscription()
  activeCartID: string | undefined
  activeUserEmail: string | undefined
  userName: string | undefined
  constants = MbConstants
  shouldLeavePage = false
  isPurchaseCheckoutType = false
  checkoutTypes = MbCheckoutTypes
  supportsGuestCheckout: boolean | undefined
  supportsAccountCreation: boolean | undefined

  protected constructor (protected activatedRoute: ActivatedRoute,
                         protected router: Router,
                         protected mbCheckoutStepService: MbCheckoutStepService,
                         protected academicDegreesConnector: MbAcademicDegreesConnector,
                         protected jobTitlesConnector: MbJobTitlesConnector,
                         protected titlesConnector: MbSalutationConnector,
                         protected checkoutContactDetailsConnector: MbCheckoutContactDetailsConnector,
                         protected activeCartService: ActiveCartFacade,
                         protected cdRef: ChangeDetectorRef,
                         protected mbCheckoutNavigationService: MbCheckoutNavigationService,
                         protected globalMessageService: GlobalMessageService,
                         protected mbCheckoutContactDetailsService: MbCheckoutContactDetailsFacade,
                         protected baseSiteService: BaseSiteService,
                         protected eventService: EventService) { }

  ngOnInit () {
    this.subscription?.add(this.activeCartService.getActive().pipe(
      filter(isNotNullable),
      map(cart => {
        if (this.shouldLeavePage) {
          this.shouldLeavePage = false
          this.mbCheckoutStepService.next(this.activatedRoute)
        }
        const checkoutType = cart.checkoutStatus?.checkoutType
        if (checkoutType === this.checkoutTypes.CHECKOUT_TYPE_D || checkoutType === this.checkoutTypes.CHECKOUT_TYPE_E) {
          this.isPurchaseCheckoutType = true
        }
        this.userName = cart?.user?.name
        this.activeUserEmail = cart?.user?.uid !== OCC_USER_ID_ANONYMOUS ?
          cart?.user?.name !== OCC_USER_ID_GUEST ? cart?.user?.uid : cart?.user?.uid?.split('|')?.slice(1)?.join('|')
          : undefined
        this.isEmailDisabled()
        return cart?.user?.uid === OCC_USER_ID_ANONYMOUS || cart?.user?.name === OCC_USER_ID_GUEST ? cart?.guid || '' : cart?.code || ''
      }),
      filter(isNotNullable),
      switchMap(cartID => {
        this.activeCartID = cartID
        return this.checkoutContactDetailsConnector.get(this.activeCartID)
      })
    ).subscribe(form => {
      if (form && Object.keys(form)?.length) {
        this.contactDetails = form
        this.contactDetailsForm = this.convertCheckoutContactDetailsModel2Form()
        this.isEmailDisabled()
        this.cdRef.detectChanges()
      }
    }))

    this.subscription?.add(this.eventService.get(CheckoutContactDetailsSetEvent).subscribe(payload => {
      this.shouldLeavePage = !!payload?.isSuccessful
      this.activeCartService.reloadActiveCart()

      if (!payload?.isSuccessful) {
        console.error(payload?.error)
        this.globalMessageService.add({ key: 'contact.error' }, GlobalMessageType.MSG_TYPE_ERROR)
      }
    }))

    this.baseSiteService.get().pipe(
      take(1),
      tap(activeBaseSite => this.supportsGuestCheckout = activeBaseSite?.supportingGuestCheckout || false),
      tap(activeBaseSite => this.supportsAccountCreation = activeBaseSite?.supportingAccountCreation || false)
    ).subscribe()
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  next () {
    if (this.supportsAccountCreation && !this.supportsGuestCheckout) {
      this.mbCheckoutStepService.next(this.activatedRoute)
    } else if (!this.contactDetailsForm.valid) {
      this.contactDetailsForm.markAllAsTouched()
    } else {
      this.postForm()
    }
  }

  back () {
    this.mbCheckoutNavigationService.navigateBackFromContactDetails()
  }

  postForm () {
    this.convertCheckoutContactDetailsForm2Model()
    this.mbCheckoutContactDetailsService.createAndSetContactDetails(this.contactDetails)
  }

  getEmailValue () {
    return this.contactDetails?.email ? this.contactDetails.email : this.activeUserEmail ? this.activeUserEmail : ''
  }

  isEmailDisabled () {
    if (this.contactDetails?.email && (this.userName === OCC_USER_ID_GUEST || this.userName === OCC_USER_ID_ANONYMOUS)) {
      this.contactDetailsForm?.get(this.constants.EMAIL_CODE)?.enable()
      return false
    } else if (this.activeUserEmail) {
      this.contactDetailsForm?.patchValue({ email: this.activeUserEmail })
      this.contactDetailsForm?.get(this.constants.EMAIL_CODE)?.disable()
      return true
    } else {
      this.contactDetailsForm?.get(this.constants.EMAIL_CODE)?.enable()
      return false
    }
  }

  isFormDisabled () {
    if (this.supportsAccountCreation && !this.supportsGuestCheckout) {
      this.contactDetailsForm.disable()
      return true
    }
    return false
  }

  abstract convertCheckoutContactDetailsModel2Form(): UntypedFormGroup

  abstract convertCheckoutContactDetailsForm2Model(): void
}
